import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { BarChart3, Clock, AlertTriangle, TrendingUp, Download, MapPin } from 'lucide-react';

export default function Reports() {
  const [report, setReport] = useState(null);
  const [locations, setLocations] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [month, selectedLocation]);

  const fetchLocations = async () => {
    try {
      const res = await api.get('/locations');
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);
      const res = await api.get(`/reports/hours?${params.toString()}`);
      setReport(res.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams({ month });
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);
      const response = await api.get(`/reports/hours/export/pdf?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_-_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const totalPresent = report?.employees?.reduce((s, e) => s + e.days_present, 0) || 0;
  const avgEfficiency = report?.employees?.length > 0
    ? Math.round(report.employees.reduce((s, e) => s + e.efficiency, 0) / report.employees.length)
    : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Work Hours Report</h1>
          <p className="text-slate-500">Monthly attendance and hours analysis</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[180px]" data-testid="report-month-input" />
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-[180px]" data-testid="report-location-filter">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2" data-testid="report-export-pdf-btn">
            <Download className="w-4 h-4" />
            PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Working Days</p>
                <p className="text-2xl font-bold">{report?.total_working_days || 0}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Present Days</p>
                <p className="text-2xl font-bold">{totalPresent}</p>
              </div>
              <Clock className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Efficiency</p>
                <p className="text-2xl font-bold">{avgEfficiency}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-[#FF9933]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Table */}
      <Card className="bg-white border shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="report-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Employee</TableHead>
                  <TableHead className="font-semibold">Code</TableHead>
                  <TableHead className="font-semibold">Location</TableHead>
                  <TableHead className="font-semibold text-center">Present</TableHead>
                  <TableHead className="font-semibold text-center">Absent</TableHead>
                  <TableHead className="font-semibold text-center">Paid Leave</TableHead>
                  <TableHead className="font-semibold text-center">Unpaid Leave</TableHead>
                  <TableHead className="font-semibold text-center">Hours Worked</TableHead>
                  <TableHead className="font-semibold text-center">Expected</TableHead>
                  <TableHead className="font-semibold text-center">Overtime</TableHead>
                  <TableHead className="font-semibold text-center">Late</TableHead>
                  <TableHead className="font-semibold text-center">Efficiency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : !report?.employees?.length ? (
                  <TableRow><TableCell colSpan={12} className="text-center py-8 text-slate-500">No data for this month</TableCell></TableRow>
                ) : (
                  report.employees.map((emp) => (
                    <TableRow key={emp.employee_id} data-testid={`report-row-${emp.employee_id}`}>
                      <TableCell className="font-medium">{emp.employee_name}</TableCell>
                      <TableCell className="font-mono text-[#FF9933]">{emp.employee_code}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{emp.location_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-[#138808] hover:bg-[#107307]">{emp.days_present}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {emp.days_absent > 0 ? (
                          <Badge variant="destructive">{emp.days_absent}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {emp.paid_leave_days > 0 ? (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{emp.paid_leave_days}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {emp.unpaid_leave_days > 0 ? (
                          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">{emp.unpaid_leave_days}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-medium">{emp.total_hours_worked}</TableCell>
                      <TableCell className="text-center text-slate-500">{emp.expected_hours}</TableCell>
                      <TableCell className="text-center text-blue-600">{emp.overtime}</TableCell>
                      <TableCell className="text-center">
                        {emp.late_days > 0 ? (
                          <span className="flex items-center justify-center gap-1 text-red-500"><AlertTriangle className="w-3 h-3" />{emp.late_days}</span>
                        ) : <span className="text-slate-400">0</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${emp.efficiency >= 90 ? 'bg-green-500' : emp.efficiency >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(100, emp.efficiency)}%` }} />
                          </div>
                          <span className="text-xs font-medium">{emp.efficiency}%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
